import http from '../http-common';
import { buildRunOptions } from '../utils/engineRules';
import { buildRunOptionsBody } from './analysisRunOptions';

const API_URL_PATH = 'videos';

class VideosDataService {
  uploadVideo(media, socketId, callId, candidateId) {
    const handlerKeys = {
      audio: {
        candidate: 'audioCandidate',
        interviewer: 'audioInterviewer',
      },
      video: {
        candidate: 'videoCandidate',
        interviewer: 'videoInterviewer',
      },
    };

    const form = new FormData();

    media.forEach((res) => {
      form.append(handlerKeys[res.type][res.source], res.blob);
    });

    form.append('callId', callId);
    form.append('socketId', socketId);
    form.append('candidateId', candidateId);

    const headers = {
      'Content-Type': 'multipart/form-data',
    };

    return http.post(API_URL_PATH + '/humans', form, { headers: headers });
  }

  uploadVideoAvatar(media, interviewId, candidateId) {
    const handlerKeys = {
      audio: {
        candidate: 'audioCandidate',
      },
      video: {
        candidate: 'videoCandidate',
      },
    };

    const form = new FormData();

    media.forEach((res) => {
      form.append(handlerKeys[res.type][res.source], res.blob);
    });

    form.append('interviewId', interviewId);
    form.append('candidateId', candidateId);

    const headers = {
      'Content-Type': 'multipart/form-data',
    };

    return http.post(API_URL_PATH + '/avatars', form, { headers: headers });
  }

  /**
   * Queue a re-analysis of an interview.
   * @param {number} interviewId
   * @param {{ socketId?: string, analysisTypeId?: number }} payload
   * @param {{ batch?: string, note?: string, promoteOnSuccess?: boolean }} [runOptions] optional
   *   per-run options persisted on the ledger
   * @returns {Promise}
   */
  reanalyzeInterview(interviewId, payload = {}, runOptions = null) {
    return http.post(`videos/reanalyze/${interviewId}`, {
      ...payload,
      ...buildRunOptionsBody(runOptions),
    });
  }

  /**
   * Engine catalogue for the launch dialogs (M3 contract 8): `GET videos/engines` resolves
   * `{ engines: [{ name, label, description, requires, implied_by, degraded_without, sources }],
   *   selectable: { interview: [...], manual_analysis: [...] } }` inside the usual envelope.
   * @returns {Promise}
   */
  getEngines() {
    return http.get(`${API_URL_PATH}/engines`);
  }

  /**
   * Queue a re-analysis of an interview with per-run engines (M3 contract 8).
   * The `runOptions` block travels camelCase (`{ engines, configVersionId?, batch?, note?,
   * promoteOnSuccess? }`); batch/note/promote are also sent flat for the M1 controllers.
   * @param {number} interviewId
   * @param {{ analysisTypeId?: number, socketId?: string, runOptions: { engines: string[],
   *   configVersionId?: string|null, batch?: string|null, note?: string|null,
   *   promoteOnSuccess?: boolean|null } }} params
   * @returns {Promise} 202 with `{ run_id, engines_auto_added }` in `data`
   */
  reanalyze(interviewId, { analysisTypeId, socketId, runOptions } = {}) {
    const body = {};
    if (analysisTypeId !== undefined && analysisTypeId !== null) {
      body.analysisTypeId = analysisTypeId;
    }
    if (typeof socketId === 'string' && socketId) body.socketId = socketId;
    const options = buildRunOptions(runOptions || {});
    return http.post(`${API_URL_PATH}/reanalyze/${interviewId}`, {
      ...body,
      ...buildRunOptionsBody(options),
      runOptions: options,
    });
  }
}

export default new VideosDataService();
